import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Typography, Space, Tag, Divider, Spin, Alert, List, Tooltip, Pagination, theme } from 'antd';
import {
  PlusOutlined,
  FileOutlined,
  BookOutlined,
  FolderOpenOutlined,
  StarOutlined,
  BulbOutlined,
  ThunderboltOutlined,
  CodeOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('WelcomeViewer');
import { telemetryService } from '../../api';
import useTabManagerStore from '../../store/tabManagerStore';
import PipelineIcon from '../../assets/icons/pipeline.svg?react';
import WorkflowIcon from '../../assets/icons/workflow.svg?react';

const { Title, Paragraph, Text } = Typography;

// Type definitions
interface WelcomeViewerProps {
  onCreateTab: (type: string) => void;
}

interface ReleaseHighlight {
  icon: string;
  iconColor: string;
  title: string;
  description: string;
}

interface ReleaseNotes {
  version: string;
  releaseDate: string;
  highlights: ReleaseHighlight[];
}

const WelcomeContainer = styled.div`
  height: 100%;
  padding: 24px;
  overflow-y: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const WelcomeCard = styled(Card)`
  height: 100%;
  border-radius: 12px;
  box-shadow: var(--shadow-base);
  transition: all 0.3s ease;
  border: 1px solid var(--color-border);

  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-2px);
  }

  .ant-card-head {
    border-bottom: 2px solid var(--color-border-secondary);
  }

  .ant-card-body {
    height: calc(100% - 57px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

const CreateButton = styled(Button)`
  height: 60px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const HighlightItem = styled.div<{ $bgColor?: string; $hoverColor?: string; $borderColor?: string }>`
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const PlaceholderCard = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #8c8c8c;

  .anticon {
    font-size: 48px;
    margin-bottom: 16px;
    color: #d9d9d9;
  }
`;

const IconContainer = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  margin-right: 8px;

  svg {
    width: 20px !important;
    height: 20px !important;
  }
`;

const WelcomeViewer: React.FC<WelcomeViewerProps> = ({ onCreateTab }) => {
  const { token } = theme.useToken();
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNotes | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  
  // Get file history from tab manager store
  const { getRecentFiles, openTab } = useTabManagerStore((state) => ({
    getRecentFiles: state.getRecentFiles,
    openTab: state.openTab,
  }));
  
  const recentFiles = getRecentFiles(20); // Get last 20 recent files
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5; // Show 5 items per page
  
  // Calculate paginated files
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedFiles = recentFiles.slice(startIndex, endIndex);

  const handleCreateNew = (type: string): void => {
    onCreateTab(type);
  };

  const handleOpenRecentFile = (file: any): void => {
    // Open the file using the stored path if available, otherwise use the id
    const filePath = file.path || file.id;
    openTab(file.id, file.type, file.name, undefined, filePath, false);
    
    // Track the action
    telemetryService.trackEvent('recent-file-opened', {
      fileName: file.name,
      fileType: file.type,
      filePath: filePath,
      lastOpened: file.lastOpened
    });
  };

  const formatLastOpened = (lastOpened: string): string => {
    const date = new Date(lastOpened);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInMinutes < 5) {
      return t('welcome.justNow');
    } else if (diffInHours < 1) {
      return t('welcome.minutesAgo', { minutes: diffInMinutes });
    } else if (diffInHours < 24) {
      const remainingMinutes = diffInMinutes % 60;
      if (remainingMinutes === 0) {
        return t('welcome.hoursAgo', { hours: diffInHours });
      } else {
        return t('welcome.hoursMinutesAgo', { hours: diffInHours, minutes: remainingMinutes });
      }
    } else {
      return t('welcome.daysAgo', { days: diffInDays });
    }
  };

  const formatExactTime = (lastOpened: string): string => {
    const date = new Date(lastOpened);
    return date.toLocaleString();
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pipeline':
        return <PipelineIcon />;
      case 'workflow':
        return <WorkflowIcon />;
      case 'markdown':
        return <FileOutlined />;
      default:
        return <FileOutlined />;
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Fetch release notes from local asset file
  useEffect(() => {
    const fetchReleaseNotes = async (): Promise<void> => {
      try {
        setLoading(true);
        telemetryService.trackEvent('release-notes-fetch-start');
        
        // Use direct fetch for local assets since apiClient is designed for API endpoints
        const baseUrl = window.location.origin;
        const response = await fetch(`${baseUrl}/assets/release-notes.json`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data: ReleaseNotes = await response.json();
        setReleaseNotes(data);
        
        telemetryService.trackEvent('release-notes-fetch-success', {
          version: data.version,
          featuresCount: data.highlights?.length || 0
        });
      } catch (err) {
        telemetryService.trackError(err as Error, 'release-notes-fetch', {
          context: 'welcome-viewer-asset-fetch',
          fallback: 'using-static-data'
        });
        logger.warn('Could not fetch release notes from asset file, using fallback:', err);
        // Fallback to static data if asset file is not available
        setReleaseNotes({
          version: 'v2.1.0',
          releaseDate: 'March 15, 2024',
          highlights: [
            {
              icon: 'ThunderboltOutlined',
              iconColor: '#52c41a',
              title: 'Lightning Fast Processing',
              description: 'New optimized engine delivers 3x faster data transformations',
            },
            {
              icon: 'BulbOutlined',
              iconColor: '#faad14',
              title: 'Smart Auto-Complete',
              description: 'AI-powered suggestions for your data pipeline steps',
            },
            {
              icon: 'DatabaseOutlined',
              iconColor: '#1890ff',
              title: 'Enhanced Connectors',
              description: 'Support for 50+ new data sources and destinations',
            },
            {
              icon: 'CodeOutlined',
              iconColor: '#722ed1',
              title: 'Custom Functions',
              description: 'Write and share custom transformation functions',
            },
          ],
        });
        setError('Using default release notes');
      } finally {
        setLoading(false);
      }
    };

    fetchReleaseNotes();
  }, []);

  // Helper function to render icons based on string name
  const renderIcon = (iconName: string, color: string): React.ReactNode => {
    const iconProps = { style: { color } };
    switch (iconName) {
      case 'ThunderboltOutlined':
        return <ThunderboltOutlined {...iconProps} />;
      case 'BulbOutlined':
        return <BulbOutlined {...iconProps} />;
      case 'DatabaseOutlined':
        return <DatabaseOutlined {...iconProps} />;
      case 'CodeOutlined':
        return <CodeOutlined {...iconProps} />;
      default:
        return <StarOutlined {...iconProps} />;
    }
  };

  return (
    <WelcomeContainer>
      <div style={{ textAlign: 'center', marginBottom: 24, flexShrink: 0 }}>
        <Title level={1} style={{ color: 'var(--color-primary)', marginBottom: 8, fontSize: '2.5rem' }}>
          {t('welcome.title', { appName: currentTheme.branding.appName })}
        </Title>
        <Paragraph style={{ fontSize: 16, color: 'var(--color-text-secondary)', maxWidth: 600, margin: '0 auto' }}>
          {t('welcome.description')}
        </Paragraph>
      </div>

      <div style={{ flex: 1, display: 'flex' }}>
        <Row gutter={[24, 24]} style={{ width: '100%', flex: 1 }}>
          {/* Top Left - Create New */}
          <Col xs={24} lg={12}>
            <WelcomeCard
              title={
                <Space>
                  <PlusOutlined style={{ color: '#52c41a' }} />
                  <span>{t('welcome.createNew')}</span>
                </Space>
              }
              variant="borderless"
            >
              <div style={{ flex: 1 }}>
                <Paragraph style={{ marginBottom: 24, color: '#666' }}>
                  {t('welcome.startBuilding')}
                </Paragraph>

                <CreateButton
                  type="primary"
                  size="large"
                  icon={
                    <IconContainer>
                      <PipelineIcon />
                    </IconContainer>
                  }
                  onClick={() => handleCreateNew('pipeline')}
                  block
                >
                  {t('welcome.newPipeline')}
                </CreateButton>

                <CreateButton
                  size="large"
                  icon={
                    <IconContainer>
                      <WorkflowIcon />
                    </IconContainer>
                  }
                  onClick={() => handleCreateNew('workflow')}
                  block
                >
                  {t('welcome.newWorkflow')}
                </CreateButton>

                <CreateButton
                  size="large"
                  icon={<FileOutlined />}
                  onClick={() => handleCreateNew('markdown')}
                  block
                >
                  {t('welcome.newMarkdown')}
                </CreateButton>
              </div>
            </WelcomeCard>
          </Col>

          {/* Top Right - What's New */}
          <Col xs={24} lg={12}>
            <WelcomeCard
              title={
                <Space>
                  <StarOutlined style={{ color: '#faad14' }} />
                  <span>{t('welcome.whatsNew')}</span>
                  {releaseNotes && <Tag color="red">{releaseNotes.version}</Tag>}
                </Space>
              }
              variant="borderless"
              extra={
                <Button type="link" size="small">
                  {t('welcome.viewAllReleaseNotes')} →
                </Button>
              }
            >
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading && (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>{t('welcome.loadingReleaseNotes')}</div>
                  </div>
                )}

                {error && (
                  <Alert
                    message={t('welcome.releaseNotes')}
                    description={error}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {releaseNotes && (
                  <div>
                    <div style={{ marginBottom: 16 }}>
                      <Text type="secondary">
                        {t('welcome.released')} {releaseNotes.releaseDate}
                      </Text>
                    </div>
                    {releaseNotes.highlights.map((highlight, index) => (
                      <HighlightItem 
                        key={index}
                        $bgColor={token.colorBgTextActive}
                        $hoverColor={token.colorBgTextHover}
                        $borderColor={token.colorPrimary}
                      >
                        <Space align="start" size={8}>
                          {renderIcon(highlight.icon, highlight.iconColor)}
                          <div>
                            <div style={{ fontWeight: 500, marginBottom: 4 }}>
                              {highlight.title}
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {highlight.description}
                            </Text>
                          </div>
                        </Space>
                      </HighlightItem>
                    ))}
                  </div>
                )}
              </div>
            </WelcomeCard>
          </Col>

          {/* Bottom Left - Documentation */}
          <Col xs={24} lg={12}>
            <WelcomeCard
              title={
                <Space>
                  <BookOutlined style={{ color: '#722ed1' }} />
                  <span>{t('welcome.documentation')}</span>
                </Space>
              }
              variant="borderless"
            >
              <PlaceholderCard>
                <BookOutlined />
                <div>
                  <Title level={4} style={{ color: '#8c8c8c' }}>
                    {t('welcome.comingSoon')}
                  </Title>
                  <Paragraph style={{ color: '#bfbfbf' }}>
                    {t('welcome.documentationDescription')}
                  </Paragraph>
                </div>
              </PlaceholderCard>
            </WelcomeCard>
          </Col>

          {/* Bottom Right - Recent Files */}
          <Col xs={24} lg={12}>
            <WelcomeCard
              title={
                <Space>
                  <FolderOpenOutlined style={{ color: '#1890ff' }} />
                  <span>{t('welcome.recentFiles')}</span>
                </Space>
              }
              variant="borderless"
            >
              {recentFiles.length === 0 ? (
                <PlaceholderCard>
                  <FolderOpenOutlined />
                  <div>
                    <Title level={4} style={{ color: '#8c8c8c' }}>
                      {t('welcome.noRecentFiles')}
                    </Title>
                    <Paragraph style={{ color: '#bfbfbf' }}>
                      {t('welcome.recentFilesDescription')}
                    </Paragraph>
                  </div>
                </PlaceholderCard>
              ) : (
                <div>
                  <List
                    size="small"
                    dataSource={paginatedFiles}
                    renderItem={(file) => (
                    <List.Item
                      style={{
                        padding: '8px 0',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                      }}
                      onClick={() => handleOpenRecentFile(file)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = token.colorBgTextHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        justifyContent: 'space-between',
                        width: '100%'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            color: '#1890ff',
                            marginRight: 12,
                            flexShrink: 0
                          }}>
                            {getFileIcon(file.type)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              fontWeight: 500, 
                              fontSize: 14,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {file.name}
                            </div>
                            {file.path && (
                              <div style={{ 
                                fontSize: 11,
                                color: '#bfbfbf',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                marginTop: 2
                              }}>
                                {file.path}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0, marginLeft: 12 }}>
                          <Tooltip title={formatExactTime(file.lastOpened)} placement="bottom">
                            <Space size="small" style={{ fontSize: 12 }}>
                              <ClockCircleOutlined style={{ fontSize: 10 }} />
                              <span style={{ color: '#8c8c8c' }}>
                                {formatLastOpened(file.lastOpened)}
                              </span>
                            </Space>
                          </Tooltip>
                        </div>
                      </div>
                    </List.Item>
                  )}
                  />
                  {recentFiles.length > pageSize && (
                    <div style={{ marginTop: 16, textAlign: 'center' }}>
                      <Pagination
                        size="small"
                        current={currentPage}
                        total={recentFiles.length}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        showQuickJumper={false}
                        showTotal={(total, range) => 
                          t('welcome.paginationFiles', { 
                            start: range[0], 
                            end: range[1], 
                            total 
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </WelcomeCard>
          </Col>
        </Row>
      </div>
    </WelcomeContainer>
  );
};

export default WelcomeViewer;
